
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Search, X, Send, MessageSquare } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTicket } from 'features/support-ticket/hooks/useTicket';
import { useUpdateTicket } from 'features/support-ticket/hooks/useUpdateTicket';
import { useAdmins } from 'features/admin/hooks/useAdmins';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useState, useRef } from 'react';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';

const EditTicketPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: response, isLoading, isError, error } = useTicket(id);
  const { mutate: updateTicket, isPending } = useUpdateTicket();

  const ticket = response?.data;

  // Admin search states
  const [adminSearch, setAdminSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef(null);

  // Chat message state
  const [chatMessage, setChatMessage] = useState('');
  const chatEndRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(adminSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [adminSearch]);

  // Fetch admins
  const { data: adminsData, isLoading: isLoadingAdmins } = useAdmins({
    page: 1,
    limit: debouncedSearch.trim() ? 1000 : 99,
    search: debouncedSearch.trim() || undefined,
  });

  // Form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      status: '',
      priority: '',
      assignedTo: '',
      message: '',
      isInternal: false,
      statusNote: '',
    },
  });

  // Set initial values when ticket loads
  useEffect(() => {
    if (ticket) {
      reset({
        status: ticket.status,
        priority: ticket.priority,
        assignedTo: ticket.assignedTo?._id || '',
        message: '',
        isInternal: false,
        statusNote: '',
      });

      if (ticket.assignedTo) {
        setSelectedAdmin({
          _id: ticket.assignedTo._id,
          name: `${ticket.assignedTo.firstName || ''} ${ticket.assignedTo.lastName || ''}`.trim(),
          email: ticket.assignedTo.email,
          phoneNumber: ticket.assignedTo.phoneNumber,
        });
      }
    }
  }, [ticket, reset]);

  // Handle admin selection
  const handleSelectAdmin = (admin) => {
    setSelectedAdmin({
      _id: admin._id,
      name: `${admin.firstName || ''} ${admin.lastName || ''}`.trim() || admin.name,
      email: admin.email,
      phoneNumber: admin.phoneNumber,
    });
    setValue('assignedTo', admin._id);
    setAdminSearch('');
    setDebouncedSearch('');
    setShowDropdown(false);
  };

  // Handle removing selected admin
  const handleRemoveAdmin = () => {
    setSelectedAdmin(null);
    setValue('assignedTo', '');
    setAdminSearch('');
    setDebouncedSearch('');
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };

  // Show dropdown logic
  useEffect(() => {
    if (isFocused && adminsData?.data && !selectedAdmin) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [isFocused, adminsData, selectedAdmin]);

  // Scroll to bottom of chat on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.messages]);

  // Submit form
  const onSubmit = (data) => {
    const payload = {
      updateFrom: 'Admin',
    };

    if (data.status !== ticket.status) {
      payload.status = data.status;
      payload.statusNote = data.statusNote;
    }

    if (data.priority !== ticket.priority) {
      payload.priority = data.priority;
    }

    if (data.assignedTo && data.assignedTo !== ticket.assignedTo?._id) {
      payload.assignedTo = data.assignedTo;
    }

    if (data.message) {
      payload.message = data.message;
      payload.isInternal = data.isInternal;
    }

    updateTicket(
      { ticketId: ticket._id, data: payload },
      {
        onSuccess: () => {
          reset({
            status: data.status,
            priority: data.priority,
            assignedTo: data.assignedTo,
            message: '',
            isInternal: false,
            statusNote: '',
          });
        },
      }
    );
  };

  // Send chat message
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const payload = {
      updateFrom: 'Admin',
      message: chatMessage,
      isInternal: false,
    };

    updateTicket(
      { ticketId: ticket._id, data: payload },
      {
        onSuccess: () => {
          setChatMessage('');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <div className='text-destructive text-lg mb-4'>Error loading ticket</div>
        <p className='text-muted-foreground mb-4'>{error?.message || 'Something went wrong'}</p>
        <button
          onClick={() => navigate('/support-tickets')}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className='flex flex-col items-center justify-center h-screen'>
        <div className='text-lg mb-4'>Ticket not found</div>
        <button
          onClick={() => navigate('/support-tickets')}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-md'
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <Layout>
      <div className='min-h-screen bg-background'>

        {/* ── Sticky Header ── matches ViewTicketPage exactly */}
        <div className='bg-card border-b border-border sticky top-0 z-10'>
          <div className='container mx-auto px-4 py-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-4'>
                <button
                  onClick={() => navigate(`/support-tickets/${id}`)}
                  className='p-2 hover:bg-muted rounded-md transition-colors'
                  title='Back to ticket'
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className='text-2xl font-bold'>Edit Ticket</h1>
                  <p className='text-sm text-muted-foreground'>#{ticket.ticketNumber}</p>
                </div>
              </div>
              {/* Save button lives in header for quick access, mirrors Edit button on view page */}
              <button
                type='button'
                onClick={handleSubmit(onSubmit)}
                disabled={isPending}
                className='flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
              >
                {isPending ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white' />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Update Ticket</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Main Content ── single full-width card, matches ViewTicketPage */}
        <div className='container mx-auto px-4 py-6'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='bg-card border border-border rounded-lg p-6 space-y-6'>

              {/* ── Current Ticket Info (read-only snapshot) ── */}
              <div className='bg-muted rounded-lg p-4 space-y-2'>
                <p className='text-xs font-medium text-muted-foreground uppercase'>Current Ticket Info</p>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm'>
                  <div>
                    <span className='font-medium'>Subject:</span>{' '}
                    <span className='text-muted-foreground'>{ticket.subject}</span>
                  </div>
                  <div>
                    <span className='font-medium'>Category:</span>{' '}
                    <span className='text-muted-foreground'>{ticket.category?.replace(/_/g, ' ')}</span>
                  </div>
                  <div>
                    <span className='font-medium'>Name:</span>{' '}
                    <span className='text-muted-foreground'>
                      {ticket.customerId
                        ? `${ticket.customerId?.firstName} ${ticket.customerId?.lastName}`
                        : `${ticket.sellerId?.firstName} ${ticket.sellerId?.lastName}`}
                    </span>
                  </div>
                  <div>
                    <span className='font-medium'>Current Status:</span>{' '}
                    <span className='text-muted-foreground'>{ticket.status?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              </div>

              {/* ── Editable Fields: Status + Priority side by side ── */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Status */}
                <div>
                  <label className='text-xs font-medium text-muted-foreground uppercase block mb-2'>Status</label>
                  <select
                    {...register('status')}
                    className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
                  >
                    <option value='open'>Open</option>
                    <option value='in_progress'>In Progress</option>
                    <option value='pending_customer'>Pending Customer</option>
                    <option value='pending_seller'>Pending Seller</option>
                    <option value='resolved'>Resolved</option>
                    <option value='closed'>Closed</option>
                    <option value='reopened'>Reopened</option>
                  </select>
                  {errors.status && (
                    <p className='text-destructive text-xs mt-1'>{errors.status.message}</p>
                  )}
                </div>

               <div>
                  <label className='block text-sm font-medium mb-1'>Status Change Note</label>
                  <textarea
  {...register('statusNote', {
    required: 'Note is required',
  })}
  rows={2}
  className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
  placeholder='Add a note...'
/>

{errors.statusNote && (
  <p className='text-red-500 text-sm mt-1'>
    {errors.statusNote.message}
  </p>
)}
                </div> 

                {/* Priority */}
                <div>
                  <label className='text-xs font-medium text-muted-foreground uppercase block mb-2'>Priority</label>
                  <select
                    {...register('priority')}
                    className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
                  >
                    <option value='low'>Low</option>
                    <option value='medium'>Medium</option>
                    <option value='high'>High</option>
                    <option value='urgent'>Urgent</option>
                  </select>
                  {errors.priority && (
                    <p className='text-destructive text-xs mt-1'>{errors.priority.message}</p>
                  )}
                </div>
              </div>

              {/* ── Assign To Admin ── */}
              {/* <div>
                <label className='text-xs font-medium text-muted-foreground uppercase block mb-2'>
                  Assign To Admin
                </label>
                <input type='hidden' {...register('assignedTo')} />

                <div className='relative'>
                  <Search
                    className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                    size={16}
                  />
                  <input
                    ref={searchInputRef}
                    type='text'
                    placeholder='Search by name, email, or phone...'
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={(e) => {
                      if (!e.relatedTarget || !e.relatedTarget.closest('.admin-dropdown')) {
                        setTimeout(() => setIsFocused(false), 150);
                      }
                    }}
                    className='w-full pl-10 pr-3 py-2 text-sm border border-border rounded-md bg-background'
                    disabled={selectedAdmin !== null}
                  />

                  {showDropdown && adminsData?.data && adminsData.data.length > 0 && (
                    <div className='admin-dropdown absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto custom-scrollbar'>
                      {adminsData.data.map((admin) => (
                        <div
                          key={admin._id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectAdmin(admin);
                          }}
                          className='px-4 py-3 hover:bg-accent cursor-pointer border-b border-border last:border-b-0'
                        >
                          <div className='font-medium text-sm'>{admin.name}</div>
                          <div className='text-xs text-muted-foreground'>{admin.email}</div>
                          {admin.phoneNumber && (
                            <div className='text-xs text-muted-foreground'>{admin.phoneNumber}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {showDropdown && adminsData?.data && adminsData.data.length === 0 && (
                    <div className='admin-dropdown absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg'>
                      <div className='px-4 py-3 text-sm text-muted-foreground'>No admins found</div>
                    </div>
                  )}

                  {isLoadingAdmins && isFocused && (
                    <div className='admin-dropdown absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg'>
                      <div className='px-4 py-3 text-sm text-muted-foreground'>Searching...</div>
                    </div>
                  )}
                </div>

                {selectedAdmin && (
                  <div className='mt-3 p-3 bg-muted rounded-md border border-border'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <div className='font-medium text-sm'>{selectedAdmin.name}</div>
                        <div className='text-xs text-muted-foreground'>{selectedAdmin.email}</div>
                        {selectedAdmin.phoneNumber && (
                          <div className='text-xs text-muted-foreground'>{selectedAdmin.phoneNumber}</div>
                        )}
                      </div>
                      <button
                        type='button'
                        onClick={handleRemoveAdmin}
                        className='p-1 hover:bg-destructive/10 rounded-md transition-colors'
                        title='Remove assignment'
                      >
                        <X size={16} className='text-destructive' />
                      </button>
                    </div>
                  </div>
                )}

                {errors.assignedTo && (
                  <p className='text-destructive text-xs mt-1'>{errors.assignedTo.message}</p>
                )}
              </div> */}

              {/* ── Messages Section ── inline at bottom, matching ViewTicketPage pattern */}
              {/* <div className='space-y-3'>
                <label className='text-xs font-medium text-muted-foreground uppercase flex items-center space-x-1'>
                  <MessageSquare size={14} />
                  <span>Comments ({ticket.messages?.length || 0})</span>
                </label>

                <div className='space-y-3 max-h-96 overflow-y-auto custom-scrollbar'>
                  {ticket.messages && ticket.messages.length > 0 ? (
                    ticket.messages.map((message, index) => {
                      const isAdmin = message.adminId;
                      const isCustomer = message.customerId;
                      const isCurrentUser = isAdmin && message.adminId?._id === user?._id;

                      const senderName = isAdmin
                        ? `${message.adminId?.firstName || ''} ${message.adminId?.lastName || ''}`.trim()
                        : isCustomer
                        ? `${message.customerId?.firstName || ''} ${message.customerId?.lastName || ''}`.trim()
                        : `${message.sellerId?.firstName || ''} ${message.sellerId?.lastName || ''}`.trim();

                      const userType = isAdmin ? 'Admin' : isCustomer ? 'Customer' : 'Seller';

                      const profilePhoto = isAdmin
                        ? message.adminId?.profilePhoto
                        : isCustomer
                        ? message.customerId?.profilePhoto
                        : message.sellerId?.profilePhoto;

                      return (
                        <div key={message._id || index} className='bg-muted rounded-lg p-4'>
                          <div className='flex items-start gap-3 mb-2'>
                            <div className='flex-shrink-0'>
                              {profilePhoto ? (
                                <img
                                  src={profilePhoto}
                                  alt={senderName}
                                  className='w-10 h-10 rounded-full object-cover border border-border'
                                />
                              ) : (
                                <div className='w-10 h-10 rounded-full bg-primary/10 border border-border flex items-center justify-center text-sm font-medium'>
                                  {senderName?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                            </div>

                  
                            <div className='flex-1'>
                              <div className='flex items-center justify-between mb-1'>
                                <div className='flex items-center gap-2'>
                                  <p className='text-xs font-medium'>
                                    {userType}: {isCurrentUser ? 'You' : senderName}
                                  </p>
                                  {message.isInternal && (
                                    <span className='px-2 py-0.5 bg-yellow-500/10 text-yellow-600 text-xs rounded'>
                                      Internal
                                    </span>
                                  )}
                                </div>
                                <p className='text-xs text-muted-foreground'>
                                  {formatDateTime(message.sentAt)}
                                </p>
                              </div>

                              <p className='text-sm mb-2 break-words whitespace-pre-wrap'>{message.message}</p>

                              {message.attachments && message.attachments.length > 0 && (
                                <div className='mt-2 space-y-2'>
                                  {message.attachments.map((attachment, attIndex) => (
                                    <div key={attIndex}>
                                      {attachment.type?.startsWith('image/') ||
                                      attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                        <a
                                          href={attachment.url}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='block rounded overflow-hidden border border-border hover:border-primary/50 transition-colors'
                                        >
                                          <img
                                            src={attachment.url}
                                            alt={attachment.fileName || 'Attachment'}
                                            className='max-w-full max-h-64 object-contain'
                                          />
                                        </a>
                                      ) : (
                                        <a
                                          href={attachment.url}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='inline-flex items-center gap-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded text-xs hover:bg-primary/20 transition-colors'
                                        >
                                          <span>📎</span>
                                          <span className='font-medium'>
                                            {attachment.fileName || 'Attachment'}
                                          </span>
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className='text-center text-muted-foreground text-sm py-8 bg-muted rounded-lg'>
                      No messages yet
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                <div className='pt-3 border-t border-border'>
                  <div className='flex items-end space-x-2'>
                    <textarea
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      placeholder='Add a comment...'
                      rows={2}
                      className='flex-1 px-3 py-2 text-sm border border-border rounded-md bg-background resize-none'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                    <button
                      type='button'
                      onClick={handleSendMessage}
                      disabled={!chatMessage.trim() || isPending}
                      className='p-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
                    >
                      <Send size={18} />
                    </button>
                  </div>
                  <p className='text-xs text-muted-foreground mt-2'>
                    Press Enter to send, Shift+Enter for new line
                  </p>
                </div>
              </div> */}

              {/* ── Footer Actions ── */}
              {/* <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
                <button
                  type='button'
                  onClick={() => navigate(`/support-tickets/${id}`)}
                  className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isPending}
                  className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2'
                >
                  {isPending ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white' />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      <span>Update Ticket</span>
                    </>
                  )}
                </button>
              </div> */}

            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditTicketPage;